import { useMemo } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { useAppData } from "@/contexts/AppDataContext";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { productApi } from "@/resources/product";

interface KPICardProps {
  label: string;
  value: string;
  change: number;
  changeLabel: string;
  tooltip: string;
  isLoading?: boolean;
}

function KPICard({ label, value, change, changeLabel, tooltip, isLoading }: KPICardProps) {
  const isPositive = change > 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  return (
    <Card className="flex-1 min-w-0">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <p className="text-sm text-muted-foreground">{label}</p>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger className="cursor-help" onClick={(e) => e.preventDefault()}>
                <Info className="h-3.5 w-3.5 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {isLoading ? (
          <div className="h-8 w-24 rounded bg-muted animate-pulse mb-3" />
        ) : (
          <p className="text-2xl font-semibold text-foreground tracking-tight">{value}</p>
        )}
        {change !== 0 && !isLoading && (
          <div className="flex items-center gap-1.5 mt-3">
            <Badge
              className={`border-0 gap-1 font-medium ${
                isPositive
                  ? "bg-status-success-soft text-foreground"
                  : "bg-status-error-soft text-foreground"
              }`}
            >
              <TrendIcon className="h-3 w-3" />
              {isPositive ? "+" : ""}
              {change}%
            </Badge>
            <span className="text-xs text-muted-foreground">{changeLabel}</span>
          </div>
        )}
        {change === 0 && !isLoading && (
          <div className="mt-3 h-5" />
        )}
      </CardContent>
    </Card>
  );
}

/* ── Pearson correlation ── */
function pearson(x: number[], y: number[]): number {
  const n = x.length;
  if (n < 2) return 0;
  const mx = x.reduce((a, b) => a + b, 0) / n;
  const my = y.reduce((a, b) => a + b, 0) / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i] - mx;
    const dy = y[i] - my;
    num += dx * dy;
    dx2 += dx * dx;
    dy2 += dy * dy;
  }
  const den = Math.sqrt(dx2 * dy2);
  return den === 0 ? 0 : num / den;
}

/**
 * Maps |r| (Pearson correlation) to a letter grade A+ through F.
 *
 * |r| >= 0.95 → A+    |r| >= 0.90 → A     |r| >= 0.85 → A-
 * |r| >= 0.80 → B+    |r| >= 0.70 → B     |r| >= 0.65 → B-
 * |r| >= 0.60 → C+    |r| >= 0.50 → C     |r| >= 0.45 → C-
 * |r| >= 0.40 → D+    |r| >= 0.30 → D     |r| >= 0.25 → D-
 * |r| <  0.25 → F
 */
export function correlationToGrade(r: number | null): string | null {
  if (r === null) return null;
  const abs = Math.abs(r);
  if (abs >= 0.95) return "A+";
  if (abs >= 0.90) return "A";
  if (abs >= 0.85) return "A-";
  if (abs >= 0.80) return "B+";
  if (abs >= 0.70) return "B";
  if (abs >= 0.65) return "B-";
  if (abs >= 0.60) return "C+";
  if (abs >= 0.50) return "C";
  if (abs >= 0.45) return "C-";
  if (abs >= 0.40) return "D+";
  if (abs >= 0.30) return "D";
  if (abs >= 0.25) return "D-";
  return "F";
}

// unused but kept for potential future use
export { pearson };

function formatDate(d: Date): string {
  return d.toISOString().split("T")[0];
}

export function DashboardKPIs() {
  const { selectedBrandId } = useAppData();

  const { currentFrom, currentTo, previousFrom, previousTo, thirtyDaysAgo } = useMemo(() => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
    return {
      thirtyDaysAgo,
      currentFrom: formatDate(thirtyDaysAgo),
      currentTo: formatDate(now),
      previousFrom: formatDate(sixtyDaysAgo),
      previousTo: formatDate(thirtyDaysAgo),
    };
  }, []);

  const { data: currentSummary, isLoading: rankLoading } = rankTrackerApi.useGetBrandSummary(
    selectedBrandId,
    currentFrom,
    currentTo,
  );
  const { data: previousSummary } = rankTrackerApi.useGetBrandSummary(
    selectedBrandId,
    previousFrom,
    previousTo,
  );
  const { data: brandAbHeroAsins = [], isPending: abPending } =
    productApi.useGetBrandHeroAbTests(selectedBrandId);

  const kpis = useMemo(() => {
    // ── Incremental Revenue & Units from A/B tests ──────────────────────
    const allTests = brandAbHeroAsins.flatMap((a) => a.tests ?? []);
    const decisive = allTests.filter((t) => t.outcome && t.outcome !== "inconclusive");

    // Revenue: sum of absolute sales diffs from ALL decisive tests
    const revenueTests = decisive.filter((t) => t.amazonMetrics?.sales?.diff);
    const totalRevenue = revenueTests.reduce((sum, t) => {
      const raw = t.amazonMetrics!.sales!.diff.replace(/[^0-9.]/g, "");
      return sum + (parseFloat(raw) || 0);
    }, 0);

    // Tests that ended BEFORE the current 30-day window (= "previous period" base)
    const prevRevenueTests = revenueTests.filter(
      (t) => t.endDate && new Date(t.endDate) < thirtyDaysAgo,
    );
    const previousRevenue = prevRevenueTests.reduce((sum, t) => {
      const raw = t.amazonMetrics!.sales!.diff.replace(/[^0-9.]/g, "");
      return sum + (parseFloat(raw) || 0);
    }, 0);
    // Revenue added by tests that completed in the last 30 days
    const recentRevenue = totalRevenue - previousRevenue;
    const revenueChange =
      previousRevenue > 0 ? Math.round((recentRevenue / previousRevenue) * 100) : 0;

    // Units: sum of absolute unitsSold diffs from ALL decisive tests
    const unitsTests = decisive.filter((t) => t.amazonMetrics?.unitsSold?.diff != null);
    const totalUnits = unitsTests.reduce(
      (sum, t) => sum + Math.abs(t.amazonMetrics!.unitsSold!.diff),
      0,
    );

    const prevUnitsTests = unitsTests.filter(
      (t) => t.endDate && new Date(t.endDate) < thirtyDaysAgo,
    );
    const previousUnits = prevUnitsTests.reduce(
      (sum, t) => sum + Math.abs(t.amazonMetrics!.unitsSold!.diff),
      0,
    );
    const recentUnits = totalUnits - previousUnits;
    const unitsChange =
      previousUnits > 0 ? Math.round((recentUnits / previousUnits) * 100) : 0;

    // ── Avg Keyword Rank from brand summary ─────────────────────────────
    const currentData = currentSummary?.data ?? [];
    const previousData = previousSummary?.data ?? [];

    const avgOf = (days: typeof currentData) =>
      days.length ? days.reduce((s, d) => s + d.avgRank, 0) / days.length : null;

    const currentAvgRank = avgOf(currentData);
    const previousAvgRank = avgOf(previousData);

    // Positive change = rank number decreased = improved (lower is better)
    const rankChange =
      currentAvgRank != null && previousAvgRank != null && previousAvgRank > 0
        ? Math.round(((previousAvgRank - currentAvgRank) / previousAvgRank) * 100)
        : 0;

    return { totalRevenue, revenueChange, totalUnits, unitsChange, currentAvgRank, rankChange };
  }, [brandAbHeroAsins, currentSummary, previousSummary, thirtyDaysAgo]);

  const isLoading = rankLoading || abPending;

  const kpiData: KPICardProps[] = [
    {
      label: "Incremental Revenue",
      value: kpis.totalRevenue > 0 ? `$${Math.round(kpis.totalRevenue).toLocaleString()}` : "—",
      change: kpis.revenueChange,
      changeLabel: "vs last month",
      tooltip:
        "Total incremental revenue from decisive A/B tests (sum of absolute sales differences)",
    },
    {
      label: "Incremental Units Sold",
      value: kpis.totalUnits > 0 ? kpis.totalUnits.toLocaleString() : "—",
      change: kpis.unitsChange,
      changeLabel: "vs last month",
      tooltip: "Total incremental units sold from decisive A/B tests",
    },
    {
      label: "Avg Keyword Rank",
      value: kpis.currentAvgRank != null && kpis.currentAvgRank !== 0 ? `#${kpis.currentAvgRank.toFixed(1)}` : "—",
      change: kpis.rankChange,
      changeLabel: "vs last month",
      tooltip: "Average organic rank across all tracked keywords (lower is better)",
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {kpiData?.map((kpi) => (
        <KPICard key={kpi.label} {...kpi} isLoading={isLoading} />
      ))}
    </div>
  );
}
